// src/features/analytics/SavedQueryRail.tsx
// Right-rail detail for a saved query — view definition, run it (QueryAggregation
// with a results table), edit, delete. GetSavedQuery, DeleteSavedQuery.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { analyticsApi } from '@core/api';
import type { AggregationGroup, SavedQuery } from '@core/types/analytics';
import { AggregationTable } from './charts';
import { errMessage, makeEntity, type ScopeIds } from './helpers';

interface SavedQueryRailProps {
  queryId: string;
  scope: ScopeIds;
  onClose: () => void;
  onEdit: (query: SavedQuery) => void;
  onDeleted: () => void;
}

export function SavedQueryRail({ queryId, scope, onClose, onEdit, onDeleted }: SavedQueryRailProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState<SavedQuery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<AggregationGroup[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setResults(null);
    analyticsApi
      .GetSavedQuery(queryId)
      .then((res) => setQuery(res.query ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('analytics.queries.loadError'))))
      .finally(() => setLoading(false));
  }, [queryId, t]);

  const run = async () => {
    if (!query?.query_definition) return;
    setRunning(true);
    setError(null);
    try {
      const res = await analyticsApi.QueryAggregation({
        entity: makeEntity('', 'analytics_query', scope),
        data_source: query.query_definition,
      });
      setResults(res.result?.groups ?? []);
    } catch (e) {
      setError(errMessage(e, t('analytics.queries.runError')));
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await analyticsApi.DeleteSavedQuery(queryId);
      onDeleted();
    } catch (e) {
      setError(errMessage(e, t('analytics.queries.deleteError')));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <aside className="flex h-full w-96 flex-shrink-0 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">{t('analytics.queries.detailTitle')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {loading && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-6 animate-pulse rounded-md bg-muted" />
            <div className="h-20 animate-pulse rounded-md bg-muted" />
          </div>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        {!loading && query && (
          <>
            <div>
              <h3 className="text-base font-semibold text-foreground">{query.name}</h3>
              {query.description && <p className="mt-1 text-sm text-muted-foreground">{query.description}</p>}
              {query.is_shared && (
                <Badge variant="primary" className="mt-2">
                  {t('analytics.queries.shared')}
                </Badge>
              )}
            </div>

            <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
              <p>
                {t('analytics.query.product')}: <span className="text-foreground">{query.query_definition?.product_slug || '—'}</span>
              </p>
              <p>
                {t('analytics.query.entityType')}: <span className="text-foreground">{query.query_definition?.entity_type || '—'}</span>
              </p>
              <p>
                {t('analytics.query.metrics')}: <span className="text-foreground">{query.query_definition?.metrics?.length ?? 0}</span>
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={run} disabled={running}>
                {running ? t('analytics.queries.running') : t('analytics.queries.run')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onEdit(query)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                {t('common.delete')}
              </Button>
            </div>

            {results && (
              <div>
                <h4 className="mb-2 text-sm font-medium text-foreground">{t('analytics.queries.results')}</h4>
                <AggregationTable groups={results} empty={t('analytics.queries.noResults')} />
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t('analytics.queries.deleteConfirmTitle')}
        description={t('analytics.queries.deleteConfirmBody', { name: query?.name ?? '' })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </aside>
  );
}
